/**
 * Resolve a module permission from userProfile.supportProjectPermission.
 * Supports object maps, single permission objects, and project permission arrays.
 *
 * @param {object|array|null|undefined} supportProjectPermission
 * @param {string[]} moduleKeys e.g. ['reports', 'report']
 */
export const getSupportModulePermission = (
    supportProjectPermission,
    moduleKeys = []
) => {
    if (!supportProjectPermission || !moduleKeys?.length) return null

    const keySet = new Set(
        moduleKeys.map((key) => String(key || '').toLowerCase()).filter(Boolean)
    )

    const pickFromObject = (obj) => {
        if (!obj || typeof obj !== 'object') return null
        for (const key of keySet) {
            if (obj[key]) return obj[key]
        }
        return null
    }

    if (Array.isArray(supportProjectPermission)) {
        let fallback = null
        for (const item of supportProjectPermission) {
            if (!item || typeof item !== 'object') continue

            const key = String(
                item?.key ?? item?.module ?? item?.name ?? ''
            ).toLowerCase()
            const permission = keySet.has(key)
                ? item
                : pickFromObject(item?.permissions) ||
                  pickFromObject(item?.supportProjectPermission) ||
                  pickFromObject(item) ||
                  null

            if (!permission) continue
            if (permission.isRead) return permission
            if (!fallback) fallback = permission
        }
        return fallback
    }

    if (typeof supportProjectPermission === 'object') {
        const nested = pickFromObject(supportProjectPermission)
        if (nested) return nested

        if (
            'isRead' in supportProjectPermission ||
            'isEdit' in supportProjectPermission ||
            'isDelete' in supportProjectPermission
        ) {
            return supportProjectPermission
        }
    }

    return null
}

export const FULL_SUPPORT_MODULE_ACCESS = Object.freeze({
    canView: true,
    canEdit: true,
    canDelete: true,
})

export const NO_SUPPORT_MODULE_ACCESS = Object.freeze({
    canView: false,
    canEdit: false,
    canDelete: false,
})

/**
 * Super admin → full access.
 * Otherwise map isRead / isEdit / isDelete (edit/delete require read).
 */
export const resolveSupportModuleAccess = (userProfile, moduleKeys = []) => {
    if (!userProfile?.isSupportUser) return NO_SUPPORT_MODULE_ACCESS
    if (userProfile?.isSuperAdmin) return FULL_SUPPORT_MODULE_ACCESS

    const permission = getSupportModulePermission(
        userProfile?.supportProjectPermission,
        moduleKeys
    )
    if (!permission) return NO_SUPPORT_MODULE_ACCESS

    const canView = Boolean(permission.isRead)
    return {
        canView,
        canEdit: canView && Boolean(permission.isEdit),
        canDelete: canView && Boolean(permission.isDelete),
    }
}

/** @deprecated Prefer resolveSupportModuleAccess(...).canView */
export const canViewSupportModule = (userProfile, moduleKeys = []) =>
    resolveSupportModuleAccess(userProfile, moduleKeys).canView

export const getReportsPermission = (supportProjectPermission) =>
    getSupportModulePermission(supportProjectPermission, ['reports', 'report'])

export const getReportsAccess = (userProfile) =>
    resolveSupportModuleAccess(userProfile, ['reports', 'report'])

/** Super admin always; users/clients only when reports.isRead is true. */
export const canViewReporting = (userProfile) =>
    getReportsAccess(userProfile).canView

export const getTalentPermission = (supportProjectPermission) =>
    getSupportModulePermission(supportProjectPermission, ['talent'])

export const getTalentAccess = (userProfile) =>
    resolveSupportModuleAccess(userProfile, ['talent'])

export const canViewTalent = (userProfile) => getTalentAccess(userProfile).canView
export const canEditTalent = (userProfile) => getTalentAccess(userProfile).canEdit
export const canDeleteTalent = (userProfile) =>
    getTalentAccess(userProfile).canDelete

export const getClientsPermission = (supportProjectPermission) =>
    getSupportModulePermission(supportProjectPermission, [
        'clients',
        'client',
    ])

export const getClientsAccess = (userProfile) =>
    resolveSupportModuleAccess(userProfile, ['clients', 'client'])

export const canViewClients = (userProfile) =>
    getClientsAccess(userProfile).canView
export const canEditClients = (userProfile) =>
    getClientsAccess(userProfile).canEdit
export const canDeleteClients = (userProfile) =>
    getClientsAccess(userProfile).canDelete

export const getMarketXPermission = (supportProjectPermission) =>
    getSupportModulePermission(supportProjectPermission, [
        'market',
        'marketx',
        'inspiration',
        'inspiration-gallery',
    ])

export const getMarketXAccess = (userProfile) =>
    resolveSupportModuleAccess(userProfile, [
        'market',
        'marketx',
        'inspiration',
        'inspiration-gallery',
    ])

export const canViewMarketX = (userProfile) =>
    getMarketXAccess(userProfile).canView
export const canEditMarketX = (userProfile) =>
    getMarketXAccess(userProfile).canEdit
export const canDeleteMarketX = (userProfile) =>
    getMarketXAccess(userProfile).canDelete
